import React, { useMemo, useState } from "react";
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	LinearProgress,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	Cached,
	FilterAltOffOutlined,
	FilterAltOutlined,
} from "@mui/icons-material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import FilterBar from "../../topBarFilter";
import ButtonTime from "../../buttonTime";
import { PRESET_PADRAO, useCentralAlertas } from "../context";
import {
	SEVERIDADES,
	LABEL_SEVERIDADE,
	STATUS_INCIDENTE,
	LABEL_STATUS_INCIDENTE,
} from "../../../utils/centralDeAlertas/labels";
import VirtualizedAutocomplete from "../../virtualization/virtualizedAutocomplete";
import { SeloCliente } from "../../opcaoCliente";
import useStyles from "./styles";

// Barra de filtros da Central de Alertas no padrão das demais páginas
// internas (layoutDashboard/dashAtendimentos/relatorioWith): FilterBar
// compartilhado (components/topBarFilter — barra fixa no topo em desktop,
// SpeedDial + Drawer abaixo de 1350px), Autocomplete multiple com checkbox e
// renderTags resumido em Tooltip para cada dimensão, ButtonTime para o
// período (tempo absoluto com DateTimePicker / tempo relativo com presets) e
// botão de refresh. As opções de cliente/equipamento são REAIS (rota
// /find/centralAlertas/filtros, ver context/apiFiltros.js). O debounce de
// 200ms fica no context — aqui só chamamos atualizarFiltros.

const icone = <CheckBoxOutlineBlankIcon fontSize="small" />;
const iconeMarcado = <CheckBoxIcon fontSize="small" />;

const FiltrosDashboard = () => {
	const theme = useTheme();
	const styles = useStyles(theme);
	const [openFilter, setOpenFilter] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const [isValidRange, setIsValidRange] = useState(false);
	const open = Boolean(anchorEl);
	const {
		carregando,
		carregandoFiltros,
		clientes,
		equipamentos,
		equipamentosDisponiveis,
		filtros,
		atualizarFiltros,
		limparFiltros,
	} = useCentralAlertas();

	// Presets canônicos do ButtonTime (mesma tabela do layoutDashboard) —
	// montada a cada render para os values refletirem o "agora" do clique.
	const optionsTime = {
		0: {
			value: dayjs().subtract(5, "minute").set("second", 0).set("millisecond", 0).toDate(),
			label: "Últimos 5 minutos",
		},
		1: {
			value: dayjs().subtract(15, "minute").set("second", 0).set("millisecond", 0).toDate(),
			label: "Últimos 15 minutos",
		},
		2: {
			value: dayjs().subtract(30, "minute").set("second", 0).set("millisecond", 0).toDate(),
			label: "Últimos 30 minutos",
		},
		3: {
			value: dayjs().subtract(1, "hour").set("second", 0).set("millisecond", 0).toDate(),
			label: "Última hora",
		},
		4: {
			value: dayjs().subtract(3, "hour").set("second", 0).set("millisecond", 0).toDate(),
			label: "Últimas 3 horas",
		},
		5: {
			value: dayjs().subtract(6, "hour").set("second", 0).set("millisecond", 0).toDate(),
			label: "Últimas 6 horas",
		},
		6: {
			value: dayjs().subtract(12, "hour").set("second", 0).set("millisecond", 0).toDate(),
			label: "Últimas 12 horas",
		},
		7: {
			value: dayjs().subtract(24, "hour").set("second", 0).set("millisecond", 0).toDate(),
			label: "Últimas 24 horas",
		},
		8: {
			value: dayjs().subtract(2, "day").set("second", 0).set("millisecond", 0).toDate(),
			label: "Últimos 2 dias",
		},
		9: {
			value: dayjs().subtract(7, "day").set("second", 0).set("millisecond", 0).toDate(),
			label: "Últimos 7 dias",
		},
		10: {
			value: dayjs().subtract(30, "day").set("second", 0).set("millisecond", 0).toDate(),
			label: "Últimos 30 dias",
		},
		11: {
			value: dayjs().subtract(90, "day").set("second", 0).set("millisecond", 0).toDate(),
			label: "Últimos 90 dias",
		},
		12: {
			value: dayjs().subtract(6, "month").set("second", 0).set("millisecond", 0).toDate(),
			label: "Últimos 6 meses",
		},
	};

	const clientesSelecionados = useMemo(
		() => clientes.filter((cliente) => filtros.clientes.includes(cliente.id)),
		[clientes, filtros.clientes],
	);

	const equipamentosSelecionados = useMemo(
		() =>
			equipamentos.filter((equipamento) =>
				filtros.equipamentos.includes(equipamento.id),
			),
		[equipamentos, filtros.equipamentos],
	);

	// Ao mudar os clientes, remove dos equipamentos selecionados os que não
	// pertencem mais à seleção — num único atualizarFiltros.
	const aoMudarClientes = (evento, selecionados) => {
		const idsClientes = selecionados.map((cliente) => cliente.id);
		const equipamentosValidos = idsClientes.length
			? filtros.equipamentos.filter((equipamentoId) => {
					const equipamento = equipamentos.find(
						(item) => item.id === equipamentoId,
					);
					return equipamento && idsClientes.includes(equipamento.clienteId);
				})
			: filtros.equipamentos;
		atualizarFiltros({
			clientes: idsClientes,
			equipamentos: equipamentosValidos,
		});
	};

	// Menu do ButtonTime.
	const handleClick = (evento) => setAnchorEl(evento.currentTarget);
	const handleClose = () => setAnchorEl(null);

	const validarIntervalo = (inicio, fim) => {
		setIsValidRange(
			Boolean(inicio && fim && dayjs(inicio).isBefore(dayjs(fim))),
		);
	};

	const handleStartChange = (valor) => {
		setStartDate(valor);
		validarIntervalo(valor, endDate);
	};

	const handleEndChange = (valor) => {
		setEndDate(valor);
		validarIntervalo(startDate, valor);
	};

	// Tempo relativo: value do preset calculado no clique; fim = agora.
	const handleSelectTime = (chave) => {
		setStartDate(null);
		setEndDate(null);
		setIsValidRange(false);
		atualizarFiltros({
			selectTime: chave,
			initialDate: optionsTime[chave]?.value,
			finalDate: dayjs().set("second", 0).set("millisecond", 0).toDate(),
		});
	};

	// Tempo absoluto: intervalo fixo, preset desligado (selectTime null).
	const handleAplicar = () => {
		atualizarFiltros({
			selectTime: null,
			initialDate: dayjs(startDate).toDate(),
			finalDate: dayjs(endDate).toDate(),
		});
		handleClose();
	};

	// Reaplica o preset relativo atual (rola a janela até o agora).
	const handleRefresh = () => {
		if (filtros.selectTime !== null && filtros.selectTime !== undefined) {
			handleSelectTime(filtros.selectTime);
		}
	};

	const filtroAtivo =
		filtros.selectTime !== PRESET_PADRAO ||
		filtros.clientes.length > 0 ||
		filtros.equipamentos.length > 0 ||
		filtros.severidades.length > 0 ||
		filtros.status.length > 0;

	const actions = [
		{
			icon: <FilterAltOutlined />,
			name: "Filtro",
			onClick: () => setOpenFilter(true),
		},
		{
			icon: <Cached />,
			name: "Atualizar",
			onClick: handleRefresh,
		},
		{
			icon: <FilterAltOffOutlined />,
			name: "Limpar filtros",
			onClick: limparFiltros,
		},
	];

	// renderTags compartilhado: 1 selecionado mostra o nome; mais de 1, a
	// contagem — sempre com Tooltip listando todos (padrão relatorioWith).
	const renderTagsResumido = (obterNome, sufixoContagem) => (selecionados) => (
		<Tooltip
			title={
				selecionados.length ? (
					<Box>
						{selecionados.map((opcao, indice) => (
							<Typography key={indice} sx={styles.itemTooltip}>
								{obterNome(opcao)}
							</Typography>
						))}
					</Box>
				) : (
					`Nenhum ${sufixoContagem} selecionado`
				)
			}
		>
			<Box className="tag-resumo-filtro">
				{selecionados.length === 1 ? (
					<Typography variant="body2" noWrap sx={styles.tagResumo}>
						{obterNome(selecionados[0])}
					</Typography>
				) : (
					<Typography variant="body2" noWrap sx={styles.tagResumo}>
						{`${selecionados.length} ${sufixoContagem}s`}
					</Typography>
				)}
			</Box>
		</Tooltip>
	);

	const renderOpcaoComCheckbox = (obterNome) => (props, opcao, { selected }) => (
		<li {...props}>
			<Checkbox
				icon={icone}
				checkedIcon={iconeMarcado}
				checked={selected}
				style={{ marginRight: 8 }}
			/>
			{obterNome(opcao)}
		</li>
	);

	// Opção do filtro de cliente: o mesmo checkbox das outras dimensões, mais
	// o selo PF/PJ à direita. O `data-conteudo-rico` é obrigatório porque só
	// esta lista é virtualizada: sem ele o listBoxComponent descarta os
	// children e exibiria apenas o texto derivado da chave, perdendo o
	// checkbox junto.
	const renderOpcaoCliente = (props, opcao, { selected }) => (
		<li {...props} data-conteudo-rico="true">
			<Box sx={styles.opcaoCliente}>
				<Checkbox
					icon={icone}
					checkedIcon={iconeMarcado}
					checked={selected}
					style={{ marginRight: 8 }}
				/>
				<Typography variant="body2" sx={styles.nomeOpcaoCliente}>
					{opcao?.nome || "—"}
				</Typography>
				<SeloCliente option={opcao} />
			</Box>
		</li>
	);

	const selectorComponentLeft = (
		<Box sx={styles.boxControles}>
			<Box sx={styles.autocompleteFilter}>
				<VirtualizedAutocomplete
					multiple
					disableCloseOnSelect
					size="small"
					disabled={carregandoFiltros}
					options={clientes}
					value={clientesSelecionados}
					isOptionEqualToValue={(opcao, valor) => opcao.id === valor.id}
					getOptionLabel={(opcao) => opcao?.nome || "—"}
					onChange={aoMudarClientes}
					noOptionsText="Nenhum cliente disponível."
					renderInput={(params) => (
						<TextField {...params} label="Clientes" sx={styles.autocompleteText} />
					)}
					renderOption={renderOpcaoCliente}
					renderTags={renderTagsResumido(
						(opcao) => opcao?.nome || "—",
						"cliente",
					)}
				/>
				{carregandoFiltros && <LinearProgress />}
			</Box>

			<Box sx={styles.autocompleteFilter}>
				<Autocomplete
					multiple
					disableCloseOnSelect
					size="small"
					disabled={carregandoFiltros}
					options={equipamentosDisponiveis}
					value={equipamentosSelecionados}
					isOptionEqualToValue={(opcao, valor) => opcao.id === valor.id}
					getOptionLabel={(opcao) => opcao?.nome || opcao?.id || "—"}
					onChange={(evento, selecionados) =>
						atualizarFiltros({
							equipamentos: selecionados.map((equipamento) => equipamento.id),
						})
					}
					noOptionsText="Nenhum equipamento disponível."
					renderInput={(params) => (
						<TextField
							{...params}
							label="Equipamentos"
							sx={styles.autocompleteText}
						/>
					)}
					renderOption={renderOpcaoComCheckbox(
						(opcao) => opcao?.nome || opcao?.id || "—",
					)}
					renderTags={renderTagsResumido(
						(opcao) => opcao?.nome || opcao?.id || "—",
						"equipamento",
					)}
				/>
				{carregandoFiltros && <LinearProgress />}
			</Box>

			<Box sx={styles.autocompleteFilter}>
				<Autocomplete
					multiple
					disableCloseOnSelect
					size="small"
					options={SEVERIDADES}
					value={filtros.severidades}
					getOptionLabel={(opcao) => LABEL_SEVERIDADE[opcao] || opcao}
					onChange={(evento, selecionados) =>
						atualizarFiltros({ severidades: selecionados })
					}
					renderInput={(params) => (
						<TextField {...params} label="Severidade" sx={styles.autocompleteText} />
					)}
					renderOption={renderOpcaoComCheckbox(
						(opcao) => LABEL_SEVERIDADE[opcao] || opcao,
					)}
					renderTags={renderTagsResumido(
						(opcao) => LABEL_SEVERIDADE[opcao] || opcao,
						"severidade",
					)}
				/>
			</Box>

			<Box sx={styles.autocompleteFilter}>
				<Autocomplete
					multiple
					disableCloseOnSelect
					size="small"
					options={STATUS_INCIDENTE}
					value={filtros.status}
					getOptionLabel={(opcao) => LABEL_STATUS_INCIDENTE[opcao] || opcao}
					onChange={(evento, selecionados) =>
						atualizarFiltros({ status: selecionados })
					}
					renderInput={(params) => (
						<TextField {...params} label="Status" sx={styles.autocompleteText} />
					)}
					renderOption={renderOpcaoComCheckbox(
						(opcao) => LABEL_STATUS_INCIDENTE[opcao] || opcao,
					)}
					renderTags={renderTagsResumido(
						(opcao) => LABEL_STATUS_INCIDENTE[opcao] || opcao,
						"status",
					)}
				/>
			</Box>
		</Box>
	);

	const selectorComponentRight = (
		<Box sx={styles.boxPeriodo}>
			<ButtonTime
				open={open}
				optionsTime={optionsTime}
				selectTime={filtros.selectTime}
				handleClick={handleClick}
				anchorEl={anchorEl}
				handleClose={handleClose}
				loading={carregando}
				handleStartChange={handleStartChange}
				handleEndChange={handleEndChange}
				handleAplicar={handleAplicar}
				isValidRange={isValidRange}
				endDate={endDate}
				startDate={startDate}
				setEndDate={setEndDate}
				setStartDate={setStartDate}
				setSelectTime={() => {}}
				handleSelectTime={handleSelectTime}
				filterData={{
					initialDate: filtros.initialDate,
					finalDate: filtros.finalDate,
				}}
				DateTime={DateTimePicker}
			/>
			<Button
				size="small"
				variant="outlined"
				onClick={handleRefresh}
				disabled={filtros.selectTime === null || filtros.selectTime === undefined}
				sx={{
					...styles.buttonRefresh,
					"& .rotating": { animation: "spin 1s linear infinite" },
					"@keyframes spin": {
						"0%": { transform: "rotate(0deg)" },
						"100%": { transform: "rotate(-360deg)" },
					},
				}}
			>
				<Cached
					fontSize="small"
					sx={styles.iconeRefresh}
					className={carregando ? "rotating" : ""}
				/>
			</Button>
			<Button
				variant="outlined"
				size="small"
				startIcon={<FilterAltOffOutlined />}
				onClick={limparFiltros}
				disabled={!filtroAtivo}
				sx={styles.botaoLimpar}
			>
				Limpar filtros
			</Button>
		</Box>
	);

	return (
		<FilterBar
			actions={actions}
			type={1}
			openFilter={openFilter}
			setOpenFilter={setOpenFilter}
			selectorComponentLeft={selectorComponentLeft}
			selectorComponentRight={selectorComponentRight}
		/>
	);
};

export default FiltrosDashboard;
